import type { EpilotControlProps } from '@epilot/journey-logic-commons'
import React, { Suspense } from 'react'

import { shouldShowBlock } from '../../concorde-renderers/utils'
import { useAppStoreSelector } from '../../hooks/useAppStoreSelector'
import { selectIsBlockShown } from '../../store/selectors/runtime'

/**
 * Renders a Suspense-enabled version of Control, with Fallback being shown
 * while the actual control component is lazy-loaded.
 *
 * Useful to wrap JSON Forms control components and offer skeleton alternatives while loading.
 */
export const SuspendedControl = (
  /** Lazy loaded version of the control component */
  Control: ReturnType<typeof React.lazy>,
  /** Fallback component */
  Fallback: NonNullable<React.ReactNode>
) => {
  const Suspended = (controlProps: EpilotControlProps) => {
    const isShown = useAppStoreSelector((state) =>
      selectIsBlockShown(
        state,
        controlProps?.uischema?.id,
        controlProps?.uischema?.scope
      )
    )

    const isVisible = shouldShowBlock(
      controlProps.uischema.options?.display,
      isShown
    )

    return (
      <Suspense
        fallback={
          isVisible ? (
            typeof Fallback === 'function' ? (
              <Fallback {...controlProps} uischema={controlProps.uischema} />
            ) : (
              Fallback
            )
          ) : null
        }
      >
        <Control {...controlProps} />
      </Suspense>
    )
  }

  Suspended.displayName = 'SuspendedControl'

  return Suspended
}
